import { z } from 'zod';
import {
  MANUAL_METRIC_KEYS,
  MAX_PLAN_BYTES,
  METRIC_KEYS,
  METRIC_SCOPES,
  METRIC_SOURCES,
  METRIC_UNITS,
} from '@bekebi/domain';
import { uuidSchema } from '../common';

/**
 * Service metrics (docs/integrations/nextcloud.md §2): the latest observed value of each metric of
 * one service. Readable with `services.view`: values only — never the collector's address, token
 * or the raw answer. A metric never observed is absent (shown as "not collected"); an old value is
 * marked `stale`, never presented as current.
 */
export const serviceMetricSchema = z.object({
  key: z.enum(METRIC_KEYS as [string, ...string[]]),
  label: z.string(),
  /** `application`: the application's own figures. `host_via_application`: host figures as the
   * application reads them — not independent host monitoring. */
  scope: z.enum(METRIC_SCOPES),
  unit: z.enum(METRIC_UNITS),
  value: z.number(),
  source: z.enum(METRIC_SOURCES),
  observedAt: z.string(),
  /** A collected value older than two collection intervals (plus grace). */
  stale: z.boolean(),
});
export type ServiceMetric = z.infer<typeof serviceMetricSchema>;

export const serviceMetricsSchema = z.object({
  serviceId: uuidSchema,
  /** How often figures are collected for this service; null when nothing collects them. */
  collectionIntervalMinutes: z.number().int().positive().nullable(),
  metrics: z.array(serviceMetricSchema),
});
export type ServiceMetrics = z.infer<typeof serviceMetricsSchema>;

/**
 * A value the Admin enters by hand (`services.manage`; CLAUDE.md §13): only the keys in
 * MANUAL_METRIC_KEYS (the Storage Bucket's plan size), in whole bytes. Stored as `manual` with who
 * entered it and when; earlier entries are kept.
 */
export const manualMetricEntrySchema = z.object({
  key: z.enum(MANUAL_METRIC_KEYS),
  value: z.number().int().positive().max(MAX_PLAN_BYTES),
});
export type ManualMetricEntry = z.infer<typeof manualMetricEntrySchema>;

/**
 * Server disks of the Cloud Server (U-8 (a)): the latest reading over SSH, one entry per real
 * filesystem. Admin only (`monitoring.view`): devices and mount points are server internals. Not
 * Nextcloud user quotas and not the Storage Bucket. `stale` marks a reading older than two
 * collection intervals — it is never presented as current.
 */
export const serverFilesystemSchema = z.object({
  device: z.string(),
  mountPoint: z.string(),
  fsType: z.string(),
  totalBytes: z.number(),
  usedBytes: z.number(),
  availableBytes: z.number(),
  /** Used share of the space available to users, as `df` computes it. */
  usedPercent: z.number(),
});
export type ServerFilesystem = z.infer<typeof serverFilesystemSchema>;

export const serverDisksSchema = z.object({
  serviceId: uuidSchema,
  /** How often the disks are read; null when this service type has no disk collection. */
  collectionIntervalMinutes: z.number().int().positive().nullable(),
  observedAt: z.string().nullable(),
  stale: z.boolean(),
  filesystems: z.array(serverFilesystemSchema),
});
export type ServerDisks = z.infer<typeof serverDisksSchema>;

/**
 * Mail Server backup status (A-M6; docs/integrations/backups.md), from the backup objects seen in
 * the Storage Bucket. Readable with `services.view` (Admin and ICT): dates, times and sizes only —
 * no object key, bucket or provider detail. An arrived backup is NOT proof that it is complete or
 * restorable.
 */
export const backupDaySchema = z.object({
  date: z.string(),
  /** Null: no daily backup with this date was seen. */
  uploadedAt: z.string().nullable(),
  sizeBytes: z.number().nullable(),
});

export const backupStatusSchema = z.object({
  state: z.enum(['on_schedule', 'attention', 'unknown']),
  attentionAfterHours: z.number().int().positive(),
  /** The last successful Storage Bucket listing; null when it has never been listed. */
  lastCheckedAt: z.string().nullable(),
  /** True when that listing is out of date — then nothing current is known. */
  checkIsStale: z.boolean(),
  latestDaily: z
    .object({ backupDate: z.string(), uploadedAt: z.string(), sizeBytes: z.number() })
    .nullable(),
  latestManual: z.object({ backupDate: z.string(), uploadedAt: z.string() }).nullable(),
  /** The last BACKUP_RECENT_DAYS days, oldest first; missing days have nulls. */
  recentDays: z.array(backupDaySchema),
});
export type BackupStatus = z.infer<typeof backupStatusSchema>;
